import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar as CalendarIcon,
  Edit3,
  Plus,
  Trash2,
  CheckCircle2,
  Clock,
  AlertCircle,
  ArrowRight,
  Target,
  History,
  Share2,
  Download,
  X as XIcon,
  Send,
  MessageSquare
} from 'lucide-react';
import { format } from 'date-fns';

import {
  Match,
  MatchStatus,
  UserTier,
  ROUTE_PATHS,
  formatCurrency,
  MIN_BET_AMOUNT,
  canPlaceBet,
  toMYT
} from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useBetting } from '@/hooks/useBetting';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { IMAGES } from '@/assets/images';

// ── Country → ISO 3166-1 alpha-2 code mapping (all FIFA 2026 WC nations) ────
const COUNTRY_CODES: Record<string, string> = {
  // Group A
  'Mexico': 'MX', 'South Africa': 'ZA', 'South Korea': 'KR', 'Korea Republic': 'KR', 'Czechia': 'CZ', 'Czech Republic': 'CZ',
  // Group B
  'Canada': 'CA', 'Bosnia and Herzegovina': 'BA', 'Bosnia': 'BA', 'Qatar': 'QA', 'Switzerland': 'CH',
  // Group C
  'Brazil': 'BR', 'Morocco': 'MA', 'Haiti': 'HT', 'Scotland': 'GB-SCT',
  // Group D
  'United States': 'US', 'USA': 'US', 'Paraguay': 'PY', 'Australia': 'AU', 'Türkiye': 'TR', 'Turkey': 'TR',
  // Group E
  'Germany': 'DE', 'Curaçao': 'CW', 'Curacao': 'CW', 'Ivory Coast': 'CI', "Côte d'Ivoire": 'CI', 'Ecuador': 'EC',
  // Group F
  'Netherlands': 'NL', 'Japan': 'JP', 'Egypt': 'EG', 'Sweden': 'SE',
  // Group G
  'Belgium': 'BE', 'Iran': 'IR', 'New Zealand': 'NZ', 'Tunisia': 'TN',
  // Group H
  'Spain': 'ES', 'Cape Verde': 'CV', 'Cabo Verde': 'CV', 'Saudi Arabia': 'SA', 'Uruguay': 'UY',
  // Group I
  'France': 'FR', 'Senegal': 'SN', 'Iraq': 'IQ', 'Austria': 'AT',
  // Group J
  'Argentina': 'AR', 'Algeria': 'DZ', 'Norway': 'NO', 'Jordan': 'JO',
  // Group K
  'Portugal': 'PT', 'Congo DR': 'CD', 'DR Congo': 'CD', 'Democratic Republic of Congo': 'CD', 'Uzbekistan': 'UZ', 'Colombia': 'CO',
  // Group L
  'England': 'GB-ENG', 'Croatia': 'HR', 'Ghana': 'GH', 'Panama': 'PA',
  // Additional aliases
  'Korea DPR': 'KP', 'North Korea': 'KP', 'China': 'CN', 'China PR': 'CN',
  'Costa Rica': 'CR', 'Honduras': 'HN', 'El Salvador': 'SV', 'Jamaica': 'JM',
  'Venezuela': 'VE', 'Chile': 'CL', 'Peru': 'PE', 'Bolivia': 'BO',
  'Serbia': 'RS', 'Ukraine': 'UA', 'Romania': 'RO',
  'Denmark': 'DK', 'Poland': 'PL', 'Wales': 'GB-WLS', 'Greece': 'GR',
  'Slovenia': 'SI', 'Slovakia': 'SK', 'Hungary': 'HU', 'Iceland': 'IS',
  'Finland': 'FI', 'Russia': 'RU', 'Cameroon': 'CM', 'Nigeria': 'NG',
  'Mali': 'ML', 'Guinea': 'GN', 'Tanzania': 'TZ', 'Zambia': 'ZM',
  'Angola': 'AO', 'Kenya': 'KE', 'Mozambique': 'MZ', 'Uganda': 'UG',
  'Libya': 'LY', 'Sudan': 'SD', 'Ethiopia': 'ET', 'Comoros': 'KM',
  'Mauritania': 'MR', 'Somalia': 'SO', 'Gambia': 'GM',
  'United Arab Emirates': 'AE', 'UAE': 'AE', 'Kuwait': 'KW', 'Bahrain': 'BH',
  'Oman': 'OM', 'Syria': 'SY', 'Lebanon': 'LB', 'Palestine': 'PS',
  'Thailand': 'TH', 'Vietnam': 'VN', 'Indonesia': 'ID', 'Philippines': 'PH',
  'India': 'IN', 'Malaysia': 'MY', 'Singapore': 'SG',
  'Kyrgyzstan': 'KG', 'Tajikistan': 'TJ', 'Kazakhstan': 'KZ',
};

// Normalize ISO code for flagcdn (lowercase 2-letter; GB subdivisions → gb)
function isoToFlagCdn(code: string): string {
  if (code.startsWith('GB-')) return 'gb';
  return code.toLowerCase();
}

/**
 * Round country flag — clean circle, no 3D effect.
 * Uses flagcdn.com for real flag images.
 * Falls back to a 🌍 globe emoji if no code is mapped.
 */
function CountryFlag({ country, size = 48 }: { country: string; size?: number }) {
  const code = COUNTRY_CODES[country];
  const [imgError, setImgError] = React.useState(false);

  const circleStyle: React.CSSProperties = {
    width: size,
    height: size,
    minWidth: size,
    borderRadius: '50%',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(0,0,0,0.12)',
  };

  if (!code || imgError) {
    return (
      <div style={{ ...circleStyle, background: '#e5e7eb', fontSize: Math.round(size * 0.55), userSelect: 'none' }}>
        <span>🌍</span>
      </div>
    );
  }

  const cdnCode = isoToFlagCdn(code);
  const src = `https://flagcdn.com/256x192/${cdnCode}.png`;

  return (
    <div style={circleStyle}>
      <img
        src={src}
        alt={country}
        onError={() => setImgError(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />
    </div>
  );
}

/**
 * Edit Match Dialog for Company Level Users
 */
export function EditMatchDialog({
  match,
  isOpen,
  onClose,
  onSave,
}: {
  match: Match | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updated: Match) => void;
}) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<Match | null>(match);

  React.useEffect(() => {
    setFormData(match);
  }, [match]);

  if (!formData) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.includes('odds.')) {
      const key = name.split('.')[1] as 'home' | 'away' | 'draw';
      setFormData((prev) => prev ? ({
        ...prev,
        odds: { ...prev.odds, [key]: parseFloat(value) || 0 },
      }) : null);
    } else {
      setFormData((prev) => prev ? ({ ...prev, [name]: value }) : null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t.matches.editMatch}</DialogTitle>
          <DialogDescription>
            {t.matches.editMatchDesc}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="homeTeam" className="text-right">{t.matches.homeTeam}</Label>
            <Input
              id="homeTeam"
              name="homeTeam"
              value={formData.homeTeam}
              onChange={handleChange}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="awayTeam" className="text-right">{t.matches.awayTeam}</Label>
            <Input
              id="awayTeam"
              name="awayTeam"
              value={formData.awayTeam}
              onChange={handleChange}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="startTime" className="text-right">{t.matches.startTime}</Label>
            <Input
              id="startTime"
              name="startTime"
              type="datetime-local"
              value={formData.startTime}
              onChange={handleChange}
              className="col-span-3"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t.matches.cancel}</Button>
          <Button onClick={() => onSave(formData)}>{t.matches.saveChanges}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Transaction ID generator ────────────────────────────────────────────────
// Format: DDMMYYHHMMSSXXXXXX  (XXXXXX = global daily running number, zero-padded)
function generateTransactionId(_username: string, betTime: Date): string {
  const pad = (n: number, len = 2) => String(n).padStart(len, '0');
  const dd   = pad(betTime.getDate());
  const mm   = pad(betTime.getMonth() + 1);
  const yy   = pad(betTime.getFullYear() % 100);
  const hh   = pad(betTime.getHours());
  const min  = pad(betTime.getMinutes());
  const ss   = pad(betTime.getSeconds());
  const dateKey = `txn_seq_${dd}${mm}${yy}`;
  // Read & increment daily counter from localStorage (fallback only)
  const prev = parseInt(localStorage.getItem(dateKey) ?? '0', 10);
  const seq  = prev + 1;
  localStorage.setItem(dateKey, String(seq));
  // Clean up old keys (keep only today)
  Object.keys(localStorage)
    .filter(k => k.startsWith('txn_seq_') && k !== dateKey)
    .forEach(k => localStorage.removeItem(k));
  return `${dd}${mm}${yy}${hh}${min}${ss}${pad(seq, 6)}`;
}

// ── Bet Receipt component ────────────────────────────────────────────────────
interface ReceiptBet {
  selection: string;
  odds: number;
  amount: number;
  soldOutAmount?: number;  // portion NOT accepted due to limit
}
interface SoldOutItem {
  selection: string;
  soldOutAmount: number;
  requestedAmount: number;
  acceptedAmount: number;
  tightestTier: string;
}
interface ReceiptData {
  transactionId: string;
  dateTime: Date;
  username: string;
  match: Match;
  bets: ReceiptBet[];         // accepted bets
  soldOutItems?: SoldOutItem[]; // bets partially/fully sold out
  remark?: string;
  allSoldOut?: boolean;
}

// Generate a canvas image of the receipt for sharing/saving
function generateReceiptCanvas(data: ReceiptData): string {
  const SCALE   = 2;
  const W       = 600;
  const ROW_H   = 32;
  const PAD     = 20;
  const totalBet = data.bets.reduce((s, b) => s + b.amount, 0);

  // All bets combined for the table
  const allBets = data.bets;
  const tableRows = allBets.length;

  const INFO_H   = 160;   // header + serial/status/datetime/username/remark
  const MATCH_H  = 140;   // dark banner with flags
  const SBETS_H  = 40 + (tableRows + 1) * ROW_H + 48; // section header + rows + total
  const HEIGHT   = INFO_H + MATCH_H + SBETS_H + PAD;

  const canvas  = document.createElement('canvas');
  canvas.width  = W * SCALE;
  canvas.height = HEIGHT * SCALE;
  const ctx     = canvas.getContext('2d')!;
  ctx.scale(SCALE, SCALE);

  // ── White background ──
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, HEIGHT);

  let y = 0;

  // ── Dark header bar ──
  ctx.fillStyle = '#1a2744';
  ctx.fillRect(0, y, W, 46);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px Arial';
  ctx.textAlign = 'left';
  ctx.fillText('TRANSACTION RECEIPT', PAD + 28, y + 28);
  y += 46;

  // ── Info rows ──
  const infoRows: [string, string][] = [
    ['SERIAL',    data.transactionId],
    ['STATUS',    '✓ Success'],
    ['DATE TIME', format(toMYT(data.dateTime.toISOString()), 'yyyy-MM-dd HH:mm:ss') + ' MYT'],
    ['NICKNAME',  data.username],
    ['REMARK',    data.remark || ''],
  ];
  for (const [label, val] of infoRows) {
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(W, y);
    ctx.stroke();
    ctx.fillStyle = '#374151';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(label, PAD, y + 20);
    if (label === 'STATUS') {
      // Green pill
      ctx.fillStyle = '#22c55e';
      roundRect(ctx, 140, y + 8, 90, 22, 11);
      ctx.fillStyle = '#ffffff';
      ctx.font = '11px Arial';
      ctx.textAlign = 'left';
      ctx.fillText('✓ Success', 152, y + 22);
    } else {
      ctx.fillStyle = '#6b7280';
      ctx.font = '11px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(val, 140, y + 20);
    }
    y += 22;
  }

  // ── Match banner ──
  // Gradient background
  const grad = ctx.createLinearGradient(0, y, W, y + MATCH_H);
  grad.addColorStop(0, '#1a2744');
  grad.addColorStop(1, '#2d3e6e');
  ctx.fillStyle = grad;
  ctx.fillRect(0, y, W, MATCH_H);

  // HOME badge
  ctx.fillStyle = '#3b82f6';
  roundRect(ctx, PAD, y + 10, 52, 18, 9);
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 10px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('HOME', PAD + 26, y + 22);

  // AWAY badge
  ctx.fillStyle = '#ef4444';
  roundRect(ctx, W - PAD - 52, y + 10, 52, 18, 9);
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 10px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('AWAY', W - PAD - 26, y + 22);

  const flagY   = y + 40;
  const flagR   = 32;
  const homeCX  = W * 0.22;
  const awayCX  = W * 0.78;

  // Flags — instant emoji, no network
  drawFlagEmoji(ctx, homeCX, flagY, flagR, getCountryCode(data.match.homeTeam));
  drawFlagEmoji(ctx, awayCX, flagY, flagR, getCountryCode(data.match.awayTeam));

  // Team names
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 13px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(data.match.homeTeam, homeCX, flagY + flagR + 16);
  ctx.fillText(data.match.awayTeam, awayCX, flagY + flagR + 16);

  // VS circle
  ctx.beginPath();
  ctx.arc(W / 2, flagY, 22, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 11px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('VS', W / 2, flagY + 4);

  // Match datetime below VS
  const matchDateStr = format(toMYT(data.match.startTime), 'yyyy-MM-dd HH:mm:ss') + ' MYT';
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.font = '9px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(matchDateStr, W / 2, flagY + 44);

  // Rainbow stripe at bottom of banner
  const rainbow = ctx.createLinearGradient(0, 0, W, 0);
  rainbow.addColorStop(0,    '#ef4444');
  rainbow.addColorStop(0.2,  '#f97316');
  rainbow.addColorStop(0.4,  '#eab308');
  rainbow.addColorStop(0.6,  '#22c55e');
  rainbow.addColorStop(0.8,  '#3b82f6');
  rainbow.addColorStop(1,    '#8b5cf6');
  ctx.fillStyle = rainbow;
  ctx.fillRect(0, y + MATCH_H - 6, W, 6);
  y += MATCH_H;

  // ── SCORE BETS section header ──
  ctx.fillStyle = '#3b50a0';
  ctx.fillRect(0, y, W, 34);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 12px Arial';
  ctx.textAlign = 'left';
  ctx.fillText('🎯  SCORE BETS', PAD, y + 22);
  y += 34;

  // Table header
  ctx.fillStyle = '#f3f4f6';
  ctx.fillRect(0, y, W, ROW_H);
  ctx.strokeStyle = '#e5e7eb';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, y + ROW_H);
  ctx.lineTo(W, y + ROW_H);
  ctx.stroke();
  const COL = [PAD, W * 0.4, W * 0.6, W - PAD];
  ctx.fillStyle = '#374151';
  ctx.font = 'bold 10px Arial';
  ctx.textAlign = 'left';
  ctx.fillText('SCORE', COL[0], y + 20);
  ctx.textAlign = 'center';
  ctx.fillText('ODD', (COL[1] + COL[2]) / 2, y + 20);
  ctx.textAlign = 'right';
  ctx.fillText('BET', COL[3], y + 20);
  y += ROW_H;

  // Table rows
  for (let i = 0; i < allBets.length; i++) {
    const b = allBets[i];
    ctx.fillStyle = i % 2 === 0 ? '#ffffff' : '#f9fafb';
    ctx.fillRect(0, y, W, ROW_H);
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(0, y + ROW_H);
    ctx.lineTo(W, y + ROW_H);
    ctx.stroke();

    ctx.fillStyle = '#111827';
    ctx.font = 'bold 11px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(b.selection, COL[0], y + 20);

    ctx.fillStyle = '#3b82f6';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(b.odds.toFixed(2), (COL[1] + COL[2]) / 2, y + 20);

    ctx.fillStyle = '#374151';
    ctx.textAlign = 'right';
    ctx.fillText(b.amount.toFixed(2), COL[3], y + 20);
    y += ROW_H;
  }

  // Total bet row
  ctx.strokeStyle = '#e5e7eb';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(0, y);
  ctx.lineTo(W, y);
  ctx.stroke();
  y += 4;
  ctx.fillStyle = '#111827';
  ctx.font = 'bold 13px Arial';
  ctx.textAlign = 'left';
  ctx.fillText('🪙  Total Bet', PAD, y + 28);
  ctx.fillStyle = '#22c55e';
  ctx.font = 'bold 13px Arial';
  ctx.textAlign = 'right';
  ctx.fillText(totalBet.toFixed(2), W - PAD, y + 28);

  return canvas.toDataURL('image/png');
}

function BetReceipt({
  data,
  onBackToMatches,
  backLabel = '← Back to Bet',
  onGoToBetHistory,
}: {
  data: ReceiptData;
  onBackToMatches: () => void;
  backLabel?: string;
  onGoToBetHistory?: () => void;
}) {
  const [sharing, setSharing]     = React.useState(false);
  const [imgDataUrl, setImgDataUrl] = React.useState<string | null>(null);
  const [imgLoading, setImgLoading] = React.useState(true);

  // Generate the canvas image once on mount — synchronous, instant
  React.useEffect(() => {
    setImgLoading(true);
    try {
      const url = generateReceiptCanvas(data);
      setImgDataUrl(url);
    } catch { /* silent */ } finally {
      setImgLoading(false);
    }
  }, [data]);

  const totalBet = data.bets.reduce((s, b) => s + b.amount, 0);
  const matchDate = format(toMYT(data.match.startTime), 'yyyy-MM-dd HH:mm:ss') + ' MYT';
  const dtStr = format(toMYT(data.dateTime.toISOString()), 'yyyy-MM-dd HH:mm:ss') + ' MYT';

  // Categorise bets
  const homeBets = data.bets.filter(b => {
    if (b.selection === 'Any Other Score') return false;
    const [h, a] = b.selection.split('-').map(Number);
    return h > a;
  });
  const awayBets = data.bets.filter(b => {
    if (b.selection === 'Any Other Score') return false;
    const [h, a] = b.selection.split('-').map(Number);
    return a > h;
  });
  const drawAosBets = data.bets.filter(b => {
    if (b.selection === 'Any Other Score') return true;
    const [h, a] = b.selection.split('-').map(Number);
    return h === a;
  });
  const maxRows = Math.max(homeBets.length, awayBets.length, drawAosBets.length, 1);

  // ── Share (native Web Share API) ──
  const handleShare = async () => {
    if (!imgDataUrl) return;
    setSharing(true);
    try {
      const blob = await (await fetch(imgDataUrl)).blob();
      const file = new File([blob], `receipt_${data.transactionId}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Bet Receipt',
          text: `Receipt: ${data.transactionId}`,
          files: [file],
        });
      } else {
        // Fallback: download
        const a = document.createElement('a');
        a.href = imgDataUrl;
        a.download = `receipt_${data.transactionId}.png`;
        a.click();
      }
    } catch (_e) {
      // User cancelled — ignore
    } finally {
      setSharing(false);
    }
  };

  // ── Save image ──
  const handleSave = () => {
    if (!imgDataUrl) return;
    const a = document.createElement('a');
    a.href = imgDataUrl;
    a.download = `receipt_${data.transactionId}.png`;
    a.click();
  };

  // ── Text-only share ──
  const [textSharing, setTextSharing] = React.useState(false);
  const buildReceiptText = (): string => {
    const lines: string[] = [];
    lines.push('===== TRANSACTION RECEIPT =====');
    lines.push(`Serial   : ${data.transactionId}`);
    lines.push(`Status   : CONFIRMED`);
    lines.push(`Date/Time: ${dtStr}`);
    lines.push(`Name     : ${data.username}`);
    if (data.remark) lines.push(`Remark   : ${data.remark}`);
    lines.push(`Match    : ${data.match.homeTeam} vs ${data.match.awayTeam}`);
    lines.push('--------------------------------');
    lines.push('Score       | Odds  | Bet');
    lines.push('--------------------------------');
    data.bets.forEach(b => {
      const score = b.selection.padEnd(11);
      const odds  = b.odds.toFixed(2).padStart(5);
      const amt   = b.amount.toFixed(2).padStart(9);
      const soldOut = (b.soldOutAmount ?? 0) > 0 ? ` (+${b.soldOutAmount!.toFixed(2)} sold out)` : '';
      lines.push(`${score} | ${odds} | ${amt}${soldOut}`);
    });
    lines.push('--------------------------------');
    const total = data.bets.reduce((s, b) => s + b.amount, 0).toFixed(2);
    lines.push(`Total Bet: ${total}`);
    // Sold-out notice
    const soldOuts = data.soldOutItems ?? [];
    if (soldOuts.length > 0) {
      lines.push('================================');
      lines.push('⛔ Sold Out Notice:');
      soldOuts.forEach(si => {
        lines.push(`  ${si.selection}: ${si.soldOutAmount.toFixed(2)} sold out`);
      });
    }
    lines.push('================================');
    return lines.join('\n');
  };
  const handleTextShare = async () => {
    setTextSharing(true);
    try {
      const text = buildReceiptText();
      if (navigator.share) {
        await navigator.share({ title: 'Bet Receipt', text });
      } else {
        await navigator.clipboard.writeText(text);
        alert('Receipt copied to clipboard! Paste into WhatsApp / WeChat / Telegram.');
      }
    } catch (_e) { /* user cancelled */ } finally {
      setTextSharing(false);
    }
  };

  // ── Print ──
  const handlePrint = () => {
    if (!imgDataUrl) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>Receipt ${data.transactionId}</title>
      <style>body{margin:0;display:flex;justify-content:center;align-items:flex-start;padding:16px}
      img{max-width:100%;height:auto}@media print{body{padding:0}}</style></head>
      <body><img src="${imgDataUrl}" onload="window.print()"/></body></html>
    `);
    win.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden my-4">

        {/* ── Header bar ── */}
        <div className="flex items-center gap-2 px-4 py-3" style={{ background: '#1a2744' }}>
          <div className="w-5 h-5 rounded border border-gray-400 flex items-center justify-center">
            <div className="w-3 h-0.5 bg-gray-300" />
          </div>
          <span className="text-white font-bold text-sm tracking-wider">TRANSACTION RECEIPT</span>
        </div>

        {/* ── Info rows ── */}
        <div className="divide-y divide-gray-100">
          {([
            ['SERIAL',    data.transactionId],
            ['STATUS',    null],
            ['DATE TIME', dtStr],
            ['NAME',  data.username],
            ['REMARK',    data.remark || ''],
          ] as [string, string | null][]).map(([label, val]) => (
            <div key={label} className="flex items-center px-4 py-2.5">
              <span className="w-28 text-xs font-bold text-gray-700 shrink-0">{label}</span>
              {label === 'STATUS' ? (
                <span className="inline-flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: '#22c55e' }}>
                  ✓ Success
                </span>
              ) : (
                <span className="text-xs font-mono text-gray-500">{val}</span>
              )}
            </div>
          ))}
        </div>

        {/* ── Match card ── */}
        <div className="relative flex items-center justify-around py-6 px-4" style={{ background: 'linear-gradient(135deg, #1a2744 0%, #2d3e6e 100%)' }}>
          <div className="flex flex-col items-center gap-2">
            <span className="px-2 py-0.5 rounded text-white text-[10px] font-bold" style={{ background: '#3b82f6' }}>HOME</span>
            <CountryFlag country={data.match.homeTeam} size={52} />
            <span className="text-white font-bold text-sm text-center">{data.match.homeTeam}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="w-10 h-10 rounded-full border-2 border-white/30 flex items-center justify-center">
              <span className="text-white text-xs font-bold">VS</span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.35)' }}>
              <span className="text-white text-[10px]">🕐</span>
              <span className="text-white text-[10px] font-mono">{matchDate}</span>
            </div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <span className="px-2 py-0.5 rounded text-white text-[10px] font-bold" style={{ background: '#ef4444' }}>AWAY</span>
            <CountryFlag country={data.match.awayTeam} size={52} />
            <span className="text-white font-bold text-sm text-center">{data.match.awayTeam}</span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1.5"
            style={{ background: 'linear-gradient(to right,#ef4444,#f97316,#eab308,#22c55e,#3b82f6,#8b5cf6)' }} />
        </div>

        {/* ── Score Bets ── */}
        <div>
          <div className="flex items-center gap-2 px-4 py-2" style={{ background: '#3b50a0' }}>
            <span className="text-lg">🎯</span>
            <span className="text-white font-bold text-xs tracking-widest">SCORE BETS</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr>
                  <th className="px-3 py-1.5 text-left font-bold text-white" style={{ background: '#222' }}>Score</th>
                  <th className="px-3 py-1.5 text-center font-bold text-white" colSpan={2} style={{ background: '#3b82f6' }}>HOME</th>
                  <th className="px-3 py-1.5 text-left font-bold text-white" style={{ background: '#222' }}>Score</th>
                  <th className="px-3 py-1.5 text-center font-bold text-white" colSpan={2} style={{ background: '#ef4444' }}>AWAY</th>
                </tr>
                <tr>
                  <th className="px-3 py-1 text-center font-semibold text-gray-600 bg-gray-100"></th>
                  <th className="px-3 py-1 text-center font-semibold text-gray-600 bg-gray-100">ODD</th>
                  <th className="px-3 py-1 text-center font-semibold text-gray-600 bg-gray-100">BET</th>
                  <th className="px-3 py-1 text-center font-semibold text-gray-600 bg-gray-100"></th>
                  <th className="px-3 py-1 text-center font-semibold text-gray-600 bg-gray-100">ODD</th>
                  <th className="px-3 py-1 text-center font-semibold text-gray-600 bg-gray-100">BET</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: maxRows }).map((_, i) => {
                  const hb = homeBets[i];
                  const ab = awayBets[i];
                  return (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-1.5 font-bold text-center text-gray-800">{hb?.selection ?? ''}</td>
                      <td className="px-3 py-1.5 text-center font-mono" style={{ color: '#3b82f6' }}>{hb ? hb.odds.toFixed(2) : ''}</td>
                      <td className="px-3 py-1.5 text-center font-mono text-gray-700">
                        {hb ? (
                          <span>
                            {hb.amount.toFixed(2)}
                            {(hb.soldOutAmount ?? 0) > 0 && (
                              <span className="ml-1 text-[9px] text-red-500">(+{hb.soldOutAmount!.toFixed(2)} sold out)</span>
                            )}
                          </span>
                        ) : ''}
                      </td>
                      <td className="px-3 py-1.5 font-bold text-center text-gray-800">{ab?.selection ?? ''}</td>
                      <td className="px-3 py-1.5 text-center font-mono" style={{ color: '#ef4444' }}>{ab ? ab.odds.toFixed(2) : ''}</td>
                      <td className="px-3 py-1.5 text-center font-mono text-gray-700">
                        {ab ? (
                          <span>
                            {ab.amount.toFixed(2)}
                            {(ab.soldOutAmount ?? 0) > 0 && (
                              <span className="ml-1 text-[9px] text-red-500">(+{ab.soldOutAmount!.toFixed(2)} sold out)</span>
                            )}
                          </span>
                        ) : ''}
                      </td>
                    </tr>
                  );
                })}
                {drawAosBets.length > 0 && (
                  <tr className="border-t border-gray-200">
                    <td colSpan={6} className="px-3 py-1 text-[10px] text-gray-400 font-semibold">Draw / AOS</td>
                  </tr>
                )}
                {drawAosBets.map((b, i) => (
                  <tr key={`d${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-1.5 font-bold text-center text-gray-800" colSpan={2}>{b.selection}</td>
                    <td className="px-3 py-1.5 text-center font-mono" style={{ color: '#22c55e' }}>{b.odds.toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-center font-mono text-gray-700" colSpan={3}>
                      {b.amount.toFixed(2)}
                      {(b.soldOutAmount ?? 0) > 0 && (
                        <span className="ml-1 text-[9px] text-red-500">(+{b.soldOutAmount!.toFixed(2)} sold out)</span>
                      )}
                    </td>
                  </tr>
                ))}
                {/* ── Fully sold-out scores (0 accepted) ── */}
                {(data.soldOutItems ?? []).filter(si => si.acceptedAmount <= 0).length > 0 && (
                  <tr className="border-t-2 border-red-200">
                    <td colSpan={6} className="px-3 py-1 text-[10px] font-semibold bg-red-50" style={{color:'#dc2626'}}>⛔ Sold Out — Limit Reached</td>
                  </tr>
                )}
                {(data.soldOutItems ?? []).filter(si => si.acceptedAmount <= 0).map((si, i) => (
                  <tr key={`so${i}`} style={{ background: '#fef2f2' }}>
                    <td className="px-3 py-1.5 font-bold text-center" style={{color:'#b91c1c'}} colSpan={3}>{si.selection}</td>
                    <td className="px-3 py-1.5 text-center font-mono line-through" style={{color:'#ef4444'}} colSpan={3}>{si.requestedAmount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-sm font-bold">🪙 Total Bet</span>
            <span className="text-sm font-bold" style={{ color: '#22c55e' }}>{totalBet.toFixed(2)}</span>
          </div>
          {/* Sold-out summary banner */}
          {(data.soldOutItems ?? []).length > 0 && (
            <div className="mx-4 mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
              <span className="font-bold">⛔ Sold Out Notice:</span>{' '}
              {(data.soldOutItems ?? []).map((si, i) => (
                <span key={i}>
                  {i > 0 && ' · '}
                  <span className="font-semibold">{si.selection}</span>:
                  {' '}{si.soldOutAmount.toFixed(2)} sold out
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── Action buttons ── */}
        <div className="px-4 pb-5 pt-3 space-y-2">
          {/* Row 1: Share Image / Save / Print */}
          <div className="grid grid-cols-3 gap-2">
            {/* Share Image */}
            <button
              onClick={handleShare}
              disabled={imgLoading || sharing}
              className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
              style={{ background: '#25d366' }}
            >
              <Send className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{sharing ? 'Sharing…' : 'Share Image'}</span>
              <span className="sm:hidden">{sharing ? '…' : 'Share'}</span>
            </button>
            {/* Save */}
            <button
              onClick={handleSave}
              disabled={imgLoading || !imgDataUrl}
              className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
              style={{ background: '#3b82f6' }}
            >
              <Download className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Save Image</span>
              <span className="sm:hidden">Save</span>
            </button>
            {/* Print */}
            <button
              onClick={handlePrint}
              disabled={imgLoading || !imgDataUrl}
              className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
              style={{ background: '#6b7280' }}
            >
              <span className="text-base leading-none shrink-0">🖨️</span>
              <span className="hidden sm:inline">Print Receipt</span>
              <span className="sm:hidden">Print</span>
            </button>
          </div>
          {/* Row 2: Text Only Share */}
          <button
            onClick={handleTextShare}
            disabled={textSharing}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40"
            style={{ background: '#0088cc' }}
          >
            <MessageSquare className="w-4 h-4 shrink-0" />
            <span>{textSharing ? 'Sharing…' : 'Text Only Share (WhatsApp / WeChat / Telegram)'}</span>
          </button>
          {imgLoading && (
            <p className="text-center text-xs text-gray-400">Preparing image…</p>
          )}
          {/* Row 3: Back / Close */}
          <button
            onClick={onBackToMatches}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: '#1a2744' }}
          >
            {backLabel}
          </button>
          {/* Row 4: Go to Bet History (only for main receipt, not history modal) */}
          {onGoToBetHistory && (
            <button
              onClick={onGoToBetHistory}
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: '#3b50a0' }}
            >
              📋 Go to Bet History
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Load a real flag image from flagcdn.com (returns null on failure) ──────
function loadFlagImage(countryCode: string): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    if (!countryCode) { resolve(null); return; }
    const code = countryCode.startsWith('GB-') ? 'gb' : countryCode.toLowerCase();
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = `https://flagcdn.com/256x192/${code}.png`;
  });
}

// ── Draw a real flag image clipped to a circle ─────────────────────────────
function drawFlagImage(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
  img: HTMLImageElement | null,
  fallbackCode: string
) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = '#f0f4ff';
  ctx.fill();
  ctx.clip();
  if (img) {
    const aspect = img.naturalWidth / img.naturalHeight;
    let drawW = r * 2, drawH = r * 2;
    if (aspect > 1) { drawW = drawH * aspect; } else { drawH = drawW / aspect; }
    ctx.drawImage(img, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
  } else {
    const emoji = countryCodeToEmoji(fallbackCode);
    const fontSize = Math.round(r * 1.5);
    ctx.font = `${fontSize}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#333';
    ctx.fillText(emoji, cx, cy + fontSize * 0.05);
  }
  ctx.restore();
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = '#cccccc';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

// ── Canvas-based share image generator ─────────────────────────────────────
// Async: loads real flag images from flagcdn.com, falls back to emoji
async function generateShareCanvas(match: Match): Promise<string> {
  const homeScores = ['1-0','2-0','2-1','3-0','3-1','3-2','4-0','4-1','4-2','4-3'];
  const awayScores = ['0-1','0-2','1-2','0-3','1-3','2-3','0-4','1-4','2-4','3-4'];
  const drawScores = ['0-0','1-1','2-2','3-3','4-4'];
  const aosOdds    = match.scoreOdds?.['AOS'];

  const SCALE    = 2;              // retina
  const W        = 760;
  const ROW_H    = 38;
  const HEADER_H = 56;
  const ROWS     = 10;
  const TOP_H    = 160;            // flag + name + date area
  const BOTTOM_H = 36;
  const HEIGHT   = TOP_H + HEADER_H + ROWS * ROW_H + BOTTOM_H + 16;

  const canvas  = document.createElement('canvas');
  canvas.width  = W * SCALE;
  canvas.height = HEIGHT * SCALE;
  const ctx     = canvas.getContext('2d')!;
  ctx.scale(SCALE, SCALE);

  // ── Background ──
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, HEIGHT);

  // ── Top section: HOME badge | flags + names | AWAY badge ──
  const colW = W / 3;
  const flagY = 28;
  const flagR = 38;

  // HOME badge
  ctx.fillStyle = '#1e90ff';
  roundRect(ctx, 14, 8, 64, 22, 11);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 11px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('HOME', 14 + 32, 8 + 15);

  // AWAY badge
  ctx.fillStyle = '#e63946';
  roundRect(ctx, W - 78, 8, 64, 22, 11);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 11px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('AWAY', W - 78 + 32, 8 + 15);

  // Load real flag images from flagcdn.com
  const homeCode = COUNTRY_CODES[match.homeTeam] ?? getCountryCode(match.homeTeam);
  const awayCode = COUNTRY_CODES[match.awayTeam] ?? getCountryCode(match.awayTeam);
  const [homeImg, awayImg] = await Promise.all([
    loadFlagImage(homeCode),
    loadFlagImage(awayCode),
  ]);
  drawFlagImage(ctx, colW * 0.5, flagY + flagR, flagR, homeImg, homeCode);
  drawFlagImage(ctx, colW * 2.5, flagY + flagR, flagR, awayImg, awayCode);

  // Team names
  ctx.fillStyle = '#111111';
  ctx.font = 'bold 18px Arial';
  ctx.textAlign = 'center';
  ctx.fillText(match.homeTeam, colW * 0.5, flagY + flagR * 2 + 24);
  ctx.fillText(match.awayTeam, colW * 2.5, flagY + flagR * 2 + 24);

  // VS
  ctx.fillStyle = '#888888';
  ctx.font = 'bold 14px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('VS', W / 2, flagY + flagR + 6);

  // Date pill
  const dateStr = format(toMYT(match.startTime), 'yyyy-MM-dd  HH:mm') + ' MYT';
  ctx.fillStyle = '#f0f0f0';
  roundRect(ctx, W / 2 - 100, flagY + flagR + 18, 200, 26, 13);
  ctx.fillStyle = '#333333';
  ctx.font = '12px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('🗓 ' + dateStr, W / 2, flagY + flagR + 18 + 17);

  // ── Column headers ──
  const tableY = TOP_H;
  const col1W = 240;   // HOME
  const col2W = 280;   // DRAW
  const col3W = W - col1W - col2W; // AWAY

  // HOME header
  ctx.fillStyle = '#1e90ff';
  ctx.fillRect(0, tableY, col1W, HEADER_H);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 16px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('🏠 HOME', col1W / 2, tableY + HEADER_H / 2 + 6);

  // DRAW header
  ctx.fillStyle = '#2ecc40';
  ctx.fillRect(col1W, tableY, col2W, HEADER_H);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 16px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('— DRAW', col1W + col2W / 2, tableY + HEADER_H / 2 + 6);

  // AWAY header
  ctx.fillStyle = '#e63946';
  ctx.fillRect(col1W + col2W, tableY, col3W, HEADER_H);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 16px Arial';
  ctx.textAlign = 'center';
  ctx.fillText('✈ AWAY', col1W + col2W + col3W / 2, tableY + HEADER_H / 2 + 6);

  // ── Score rows ──
  for (let i = 0; i < ROWS; i++) {
    const ry = tableY + HEADER_H + i * ROW_H;
    const bg = i % 2 === 0 ? '#ffffff' : '#f5f5f5';
    const hs  = homeScores[i];
    const as2 = awayScores[i];
    const ds  = drawScores[i];                         // only 5 draw rows
    const ho  = match.scoreOdds?.[hs];
    const ao  = match.scoreOdds?.[as2];
    const dOdd = ds ? match.scoreOdds?.[ds] : undefined;

    // row backgrounds
    ctx.fillStyle = bg;
    ctx.fillRect(0, ry, W, ROW_H);

    // thin divider line
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, ry); ctx.lineTo(W, ry); ctx.stroke();

    // HOME: score left, odd right — split the col1W in half
    const hHalf = col1W / 2;
    ctx.fillStyle = '#333333';
    ctx.font = 'bold 13px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(hs, hHalf * 0.5, ry + ROW_H / 2 + 5);
    ctx.fillStyle = ho ? '#1e90ff' : '#bbbbbb';
    ctx.font = 'bold 14px Arial';
    ctx.fillText(ho ? ho.toFixed(2) : '–', hHalf * 1.5, ry + ROW_H / 2 + 5);

    // vertical divider between home cols
    ctx.strokeStyle = '#dddddd'; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(hHalf, ry); ctx.lineTo(hHalf, ry + ROW_H); ctx.stroke();

    // DRAW section: i < 5 → draw score + odd; i === 5 → AOS; i > 5 → empty
    const dCx = col1W + col2W / 2;
    if (i < 5 && ds) {
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 13px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(ds, dCx - col2W / 4, ry + ROW_H / 2 + 5);
      ctx.fillStyle = dOdd ? '#2ecc40' : '#bbbbbb';
      ctx.font = 'bold 14px Arial';
      ctx.fillText(dOdd ? dOdd.toFixed(2) : '–', dCx + col2W / 4, ry + ROW_H / 2 + 5);
    } else if (i === 5 && aosOdds) {
      ctx.fillStyle = '#c8860a';
      ctx.fillRect(col1W, ry, col2W, ROW_H);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 13px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('AOS', dCx - col2W / 4, ry + ROW_H / 2 + 5);
      ctx.font = 'bold 14px Arial';
      ctx.fillText(aosOdds.toFixed(2), dCx + col2W / 4, ry + ROW_H / 2 + 5);
    }
    // draw vertical divider inside draw col
    ctx.strokeStyle = '#dddddd'; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(col1W + col2W / 2, ry); ctx.lineTo(col1W + col2W / 2, ry + ROW_H); ctx.stroke();

    // AWAY: odd left, score right
    const aX = col1W + col2W;
    const aHalf = col3W / 2;
    ctx.fillStyle = ao ? '#e63946' : '#bbbbbb';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(ao ? ao.toFixed(2) : '–', aX + aHalf * 0.5, ry + ROW_H / 2 + 5);
    ctx.fillStyle = '#333333';
    ctx.font = 'bold 13px Arial';
    ctx.fillText(as2, aX + aHalf * 1.5, ry + ROW_H / 2 + 5);

    // vertical divider inside away col
    ctx.strokeStyle = '#dddddd'; ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(aX + aHalf, ry); ctx.lineTo(aX + aHalf, ry + ROW_H); ctx.stroke();

    // vertical col separators
    ctx.strokeStyle = '#cccccc'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(col1W, tableY + HEADER_H); ctx.lineTo(col1W, tableY + HEADER_H + ROWS * ROW_H); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(col1W + col2W, tableY + HEADER_H); ctx.lineTo(col1W + col2W, tableY + HEADER_H + ROWS * ROW_H); ctx.stroke();
  }

  // ── Footer ──
  const footerY = tableY + HEADER_H + ROWS * ROW_H;
  ctx.fillStyle = '#f5f5f5';
  ctx.fillRect(0, footerY, W, BOTTOM_H + 16);
  ctx.fillStyle = '#666666';
  ctx.font = '11px Arial';
  ctx.textAlign = 'left';
  ctx.fillText('⚽ FIFA World Cup 2026', 16, footerY + 22);
  ctx.textAlign = 'right';
  ctx.fillStyle = '#333333';
  ctx.font = 'bold 11px Arial';
  ctx.fillText('Odds subject to change', W - 16, footerY + 22);

  return canvas.toDataURL('image/png');
}

// ── helper: convert 2-letter country code to flag emoji (instant, no network) ─
// Each regional indicator letter = 0x1F1E6 + (charCode - 65)
function countryCodeToEmoji(code: string): string {
  if (!code || code.length < 2) return '🌐';
  const c = code.toUpperCase();
  const cp1 = 0x1F1E6 + (c.charCodeAt(0) - 65);
  const cp2 = 0x1F1E6 + (c.charCodeAt(1) - 65);
  return String.fromCodePoint(cp1, cp2);
}

// ── helper: draw flag emoji clipped to a circle (synchronous, instant) ───────
function drawFlagEmoji(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  countryCode: string
) {
  // Background circle
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = '#f0f4ff';
  ctx.fill();
  ctx.clip();
  // Draw emoji centered in the circle
  const emoji = countryCodeToEmoji(countryCode);
  const fontSize = Math.round(r * 1.5);
  ctx.font = `${fontSize}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, cx, cy + fontSize * 0.05); // slight optical correction
  ctx.restore();
  // Border ring
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.strokeStyle = '#cccccc';
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.restore();
}

// ── helper: get 2-letter country code ────────────────────────────────────────
function getCountryCode(country: string): string {
  return (COUNTRY_CODES[country] || country.slice(0, 2)).toUpperCase();
}

// ── helper: draw rounded rect fill (path only, call fillStyle before) ──────
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}

// ── Share Modal ───────────────────────────────────────────────────────────────
// Uses Web Share API (native share sheet) when available;
// falls back to Save Image + app-specific links.
function ShareMatchModal({
  match,
  imageDataUrl,
  onClose,
}: {
  match: Match;
  imageDataUrl: string | null;
  onClose: () => void;
}) {
  const matchDate = format(toMYT(match.startTime), 'yyyy-MM-dd HH:mm') + ' MYT';
  const fileName = `${match.homeTeam}_vs_${match.awayTeam}_odds.png`.replace(/\s+/g, '_');
  const { t } = useLanguage();
  const [sharing, setSharing] = React.useState(false);
  const [shareError, setShareError] = React.useState('');

  // Convert dataURL → File for Web Share API
  function dataUrlToFile(dataUrl: string, name: string): File {
    const [header, base64] = dataUrl.split(',');
    const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
    const bytes = atob(base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new File([arr], name, { type: mime });
  }

  // Native share sheet — opens WhatsApp / Telegram / WeChat / etc. directly
  const handleNativeShare = async () => {
    if (!imageDataUrl) return;
    setSharing(true);
    setShareError('');
    try {
      const file = dataUrlToFile(imageDataUrl, fileName);
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${match.homeTeam} vs ${match.awayTeam}`,
          text: `⚽ FIFA 2026 Odds\n${match.homeTeam} vs ${match.awayTeam}\n📅 ${matchDate}`,
        });
        onClose();
      } else {
        // Fallback: just download
        handleDownload();
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        setShareError('Share not supported on this device. Image saved instead.');
        handleDownload();
      }
    } finally {
      setSharing(false);
    }
  };

  const handleDownload = () => {
    if (!imageDataUrl) return;
    const a = document.createElement('a');
    a.href = imageDataUrl;
    a.download = fileName;
    a.click();
  };

  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-2xl shadow-2xl border border-border w-full max-w-sm overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-semibold text-sm">{t.matches.shareMatchOdds}</span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Preview */}
        <div className="p-3 bg-muted/20 flex items-center justify-center min-h-[120px]">
          {imageDataUrl ? (
            <img
              src={imageDataUrl}
              alt="Match odds"
              className="max-w-full rounded-lg border border-border/50 shadow"
            />
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground py-6">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-primary border-t-transparent" />
              <span className="text-xs">{t.matches.loadingFlags}</span>
            </div>
          )}
        </div>

        {/* Match subtitle */}
        <div className="px-4 py-1.5 text-center border-b border-border/40">
          <p className="font-semibold text-sm">{match.homeTeam} vs {match.awayTeam}</p>
          <p className="text-xs text-muted-foreground">{matchDate}</p>
        </div>

        {shareError && (
          <p className="px-4 pt-2 text-xs text-red-500 text-center">{shareError}</p>
        )}

        {/* Actions */}
        <div className="px-4 py-3 space-y-2">
          {/* Primary: native share sheet (WhatsApp / Telegram / WeChat appear here) */}
          {canNativeShare ? (
            <button
              onClick={handleNativeShare}
              disabled={!imageDataUrl || sharing}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 disabled:opacity-40 transition-all"
            >
              <Send className="h-4 w-4 shrink-0" />
              {sharing ? t.matches.openingShare : t.matches.shareImage}
            </button>
          ) : (
            // Desktop fallback: separate app links + download
            <div className="space-y-2">
              <button
                onClick={handleDownload}
                disabled={!imageDataUrl}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 disabled:opacity-40 transition-all"
              >
                <Download className="h-4 w-4 shrink-0" />
                {t.matches.saveImageTitle}
              </button>
              <p className="text-xs text-muted-foreground text-center">
                {t.matches.saveOpenInstructions}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Individual Match Betting Card with Input Boxes and Submit Button
 */
export function MatchBettingCard({
  match,
  onEdit,
  isLocked,
  onSubmitBets,
  initialBets,
}: {
  match: Match;
  onEdit?: () => void;
  isLocked: boolean;
  onSubmitBets: (matchId: string, bets: Array<{selection: string, amount: number, odds: number}>, remark?: string) => Promise<any>;
  /** Pre-filled bet amounts for Re-Bet flow: Record<selection, amountString> */
  initialBets?: Record<string, string>;
}) {
  const { user, isCompany } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const canBet = user?.tier === UserTier.PLAYER;
  const startTime = toMYT(match.startTime); // display time (MYT = UTC+8)
  // DB stores UTC; compare directly with Date.now() minus 2 min buffer
  const bettingCutoff = new Date(new Date(match.startTime).getTime() - 2 * 60 * 1000);
  const [betAmounts, setBetAmounts] = useState<Record<string, string>>(initialBets ?? {});
  const [remark, setRemark] = useState<string>('');

  // When initialBets is provided (Re-Bet flow), apply them once they arrive
  const appliedInitialRef = useRef(false);
  useEffect(() => {
    if (initialBets && Object.keys(initialBets).length > 0 && !appliedInitialRef.current) {
      appliedInitialRef.current = true;
      setBetAmounts(initialBets);
    }
  }, [initialBets]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeUntilCutoff, setTimeUntilCutoff] = useState<string>('');
  const [isBettingClosed, setIsBettingClosed] = useState(false);

  // ── Score limits state ──
  // scoreLimits[scoreKey] = REMAINING amount available to bet (tightest limit - already used)
  const [scoreLimits, setScoreLimits] = useState<Record<string, number>>({});
  const [scoreLimitTiers, setScoreLimitTiers] = useState<Record<string, string>>({});
  // limitsLoaded prevents flickering: cells stay as inputs until first fetch returns
  const [limitsLoaded, setLimitsLoaded] = useState(false);

  // Fetch effective remaining score limits for this match
  const fetchLimits = useCallback(async () => {
    if (!user?.id || !canBet) return;
    try {
      const { data: effData } = await supabase.functions.invoke(
        'score_limits_2026_05_12',
        { body: { action: 'effective', userId: user.id, matchId: match.id } }
      );
      setScoreLimits(effData?.limits || {});
      setScoreLimitTiers(effData?.tiers || {});
    } catch { /* silent */ } finally {
      setLimitsLoaded(true);
    }
  }, [user?.id, match.id, canBet]);

  useEffect(() => {
    if (!user?.id || !canBet) return;
    setLimitsLoaded(false);
    fetchLimits();
  }, [fetchLimits]);

  // Helper: score is "Sold Out" when remaining is 0 AND limits have loaded
  // Before first fetch completes, never mark sold out to prevent flickering
  const isScoreFull = useCallback((scoreKey: string): boolean => {
    if (!limitsLoaded) return false;
    const remaining = scoreLimits[scoreKey];
    if (remaining === undefined) return false;
    return remaining <= 0;
  }, [scoreLimits, limitsLoaded]);

  // ── Receipt state ──
  const [receiptData, setReceiptData] = useState<ReceiptData | null>(null);

  // ── Share state ──
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState<string | null>(null);

  const handleShare = useCallback(() => {
    setShareImageUrl(null); // show spinner while flags load
    setShowShareModal(true);
    generateShareCanvas(match).then(dataUrl => setShareImageUrl(dataUrl));
  }, [match]);

  // Update countdown timer
  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const timeDiff = bettingCutoff.getTime() - now.getTime();
      
      if (timeDiff <= 0) {
        setIsBettingClosed(true);
        setTimeUntilCutoff(t.matches.bettingClosed);
      } else {
        setIsBettingClosed(false);
        const minutes = Math.floor(timeDiff / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
        
        if (minutes > 60) {
          const hours = Math.floor(minutes / 60);
          const remainingMinutes = minutes % 60;
          setTimeUntilCutoff(`${hours}h ${remainingMinutes}m`);
        } else if (minutes > 0) {
          setTimeUntilCutoff(`${minutes}m ${seconds}s`);
        } else {
          setTimeUntilCutoff(`${seconds}s`);
        }
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [bettingCutoff]);

  // Get available score odds (non-zero values)
  const availableScoreOdds = useMemo(() => {
    if (!match.scoreOdds) {
      console.log('MatchBettingCard: No scoreOdds for match', match.id);
      return [];
    }
    
    console.log('MatchBettingCard: Raw scoreOdds:', match.scoreOdds);
    console.log('MatchBettingCard: 0-0 odds:', match.scoreOdds['0-0']);
    console.log('MatchBettingCard: AOS odds:', match.scoreOdds['AOS']);
    
    const filtered = Object.entries(match.scoreOdds)
      .filter(([score, odds]) => {
        const included = odds > 0;
        if (!included) {
          console.log(`MatchBettingCard: Filtering out ${score} with odds ${odds}`);
        }
        return included;
      })
      .sort(([a], [b]) => {
        // Sort by score: 0-1, 0-2, ..., 1-0, 1-1, ..., AOS
        if (a === 'AOS') return 1;
        if (b === 'AOS') return -1;
        return a.localeCompare(b);
      });
    
    console.log('MatchBettingCard: Available scores:', filtered.map(([s]) => s));
    return filtered;
  }, [match.scoreOdds, match.id]);

  const hasScoreOdds = availableScoreOdds.length > 0;

  // No blocking limit warnings — bets are partially accepted up to remaining limit.
  // Scores at 0 remaining show "Sold Out" input cell. Backend auto-caps & returns soldOutItems.

  const handleAmountChange = (scoreKey: string, value: string) => {
    setBetAmounts(prev => ({
      ...prev,
      [scoreKey]: value
    }));
  };

  const handleSubmit = async () => {
    // Collect all bets with amounts > 0, skip scores that are fully sold out
    const betsToSubmit = availableScoreOdds
      .filter(([scoreKey]) => {
        const amount = parseFloat(betAmounts[scoreKey] || '0');
        return amount > 0 && !isScoreFull(scoreKey);
      })
      .map(([scoreKey, odds]) => ({
        selection: scoreKey === 'AOS' ? 'Any Other Score' : scoreKey,
        amount: parseFloat(betAmounts[scoreKey]),
        odds: odds
      }));

    if (betsToSubmit.length === 0) {
      alert(t.matches.enterBetAmount);
      return;
    }

    setIsSubmitting(true);
    try {
      // onSubmitBets returns full server response: { bets, soldOutItems, hasSoldOut, transactionId, ... }
      const serverResult = await onSubmitBets(match.id, betsToSubmit, remark.trim());
      const serverBets: any[] = serverResult?.bets ?? (Array.isArray(serverResult) ? serverResult : []);
      const soldOutItems: SoldOutItem[] = serverResult?.soldOutItems ?? [];
      const now = new Date();
      const txnId = serverBets[0]?.transaction_id || serverResult?.transactionId
        || generateTransactionId(user?.username ?? 'USER', now);

      // Build soldOut lookup: scoreKey → soldOutAmount
      const soldOutMap: Record<string, number> = {};
      for (const si of soldOutItems) {
        const sk = si.selection === 'Any Other Score' ? 'AOS' : si.selection;
        soldOutMap[sk] = (soldOutMap[sk] || 0) + si.soldOutAmount;
      }

      // Build receipt bets using actual accepted amounts from server
      const receiptBets: ReceiptBet[] = betsToSubmit.map(b => {
        const sk = b.selection === 'Any Other Score' ? 'AOS' : b.selection;
        const serverBet = serverBets.find((sb: any) => sb.selection === b.selection);
        const acceptedAmount = serverBet ? parseFloat(serverBet.amount) : b.amount;
        return {
          selection: b.selection,
          odds: b.odds,
          amount: acceptedAmount,
          soldOutAmount: soldOutMap[sk] ?? 0,
        };
      });

      setReceiptData({
        transactionId: txnId,
        dateTime: now,
        username: (user?.nickname || user?.username) ?? '',
        match,
        bets: receiptBets,
        soldOutItems,
        remark: remark.trim(),
        allSoldOut: serverResult?.allSoldOut,
      });
      setBetAmounts({});
      setRemark('');
      // Refresh remaining limits so Avail: hints update immediately
      fetchLimits();
    } catch (error: any) {
      console.error('Failed to submit bets:', error);
      alert('Failed to submit bets: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalBetAmount = useMemo(() => {
    return Object.values(betAmounts).reduce((sum, amount) => {
      return sum + (parseFloat(amount) || 0);
    }, 0);
  }, [betAmounts]);

  const totalPotentialWin = useMemo(() => {
    return availableScoreOdds.reduce((sum, [scoreKey, odds]) => {
      const amount = parseFloat(betAmounts[scoreKey] || '0');
      return sum + (amount * odds);
    }, 0);
  }, [betAmounts, availableScoreOdds]);

  return (
    <>
    <Card className="group overflow-hidden transition-all hover:shadow-lg border-border/50 bg-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            {match.matchNumber && (
              <Badge variant="outline" className="font-mono text-xs bg-primary/10 text-primary border-primary/30">
                {match.matchNumber}
              </Badge>
            )}
            <Badge variant={match.status === MatchStatus.LIVE ? 'destructive' : 'secondary'} className="font-mono">
              {match.status === MatchStatus.LIVE ? 'LIVE' : format(startTime, 'MMM dd, HH:mm') + ' MYT'}
            </Badge>
            {hasScoreOdds && (
              <Badge variant="outline" className="text-xs">
                <Target className="w-3 h-3 mr-1" />
                {availableScoreOdds.length} {t.matches.odds}
              </Badge>
            )}
            {/* Betting Cutoff Timer — players only */}
            {canBet && (
              <Badge
                variant={isBettingClosed ? 'destructive' : 'secondary'}
                className={`text-xs ${isBettingClosed ? 'animate-pulse' : ''}`}
              >
                <Clock className="w-3 h-3 mr-1" />
                {isBettingClosed ? t.matches.bettingClosed : `${t.matches.closesIn} ${timeUntilCutoff}`}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Share button — visible to all users */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleShare}
              className="h-8 w-8 text-muted-foreground hover:text-primary"
              title="Share match odds"
            >
              <Share2 className="h-4 w-4" />
            </Button>
            {isCompany() && (
              <Button variant="ghost" size="icon" onClick={onEdit} className="h-8 w-8">
                <Edit3 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-4 text-center">
          <div className="flex flex-col items-center gap-2">
            <CountryFlag country={match.homeTeam} size={48} />
            <span className="font-semibold text-sm line-clamp-2">{match.homeTeam}</span>
          </div>
          <div className="text-xs font-bold text-muted-foreground">VS</div>
          <div className="flex flex-col items-center gap-2">
            <CountryFlag country={match.awayTeam} size={48} />
            <span className="font-semibold text-sm line-clamp-2">{match.awayTeam}</span>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 pb-3 px-3">
        {/* Score-Based Odds with Input Boxes */}
        {hasScoreOdds ? (
          <div className="space-y-3">
            {/* ── RESPONSIVE LAYOUT: stacked on mobile, side-by-side on desktop ── */}
            <div className="flex flex-col lg:flex-row gap-3 items-start w-full">

              {/* ════ TOP/LEFT TABLE: Home Win & Away Win ════ */}
              <div className="w-full lg:flex-1 overflow-x-auto">
                <table className="border-collapse w-full" style={{fontSize:'9px'}}>
                  <thead>
                    <tr>
                      <th className="text-center font-bold text-white border border-gray-400 px-1 py-1.5 whitespace-nowrap" style={{background:'#0099cc', fontSize:'9px'}}>HOME-ODD</th>
                      {canBet && <th className="text-center font-bold text-white border border-gray-400 px-1 py-1.5" style={{background:'#0099cc', fontSize:'9px'}}>{match.homeTeam.toUpperCase()}</th>}
                      <th className="text-center font-bold text-white border border-gray-400 px-1 py-1.5" style={{background:'#111', fontSize:'9px'}}>SCORE</th>
                      <th className="text-center font-bold text-white border border-gray-400 px-1 py-1.5 whitespace-nowrap" style={{background:'#cc003d', fontSize:'9px'}}>AWAY-ODD</th>
                      {canBet && <th className="text-center font-bold text-white border border-gray-400 px-1 py-1.5" style={{background:'#cc003d', fontSize:'9px'}}>{match.awayTeam.toUpperCase()}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(['1-0','2-0','2-1','3-0','3-1','3-2','4-0','4-1','4-2','4-3'] as const).map((homeScore, idx) => {
                      const awayScore = (['0-1','0-2','1-2','0-3','1-3','2-3','0-4','1-4','2-4','3-4'] as const)[idx];
                      const homeOdds = match.scoreOdds?.[homeScore];
                      const awayOdds = match.scoreOdds?.[awayScore];
                      const rowBg = idx % 2 === 0 ? '#ffffff' : '#f7f7f7';
                      return (
                        <tr key={homeScore} style={{background: rowBg}}>
                          <td className="text-center font-bold border border-gray-200 px-1 py-1 whitespace-nowrap" style={{color:'#0099cc', fontSize:'9px'}}>
                            {homeOdds ? homeOdds.toFixed(2) : '–'}
                          </td>
                          {canBet && (
                            <td className="border border-gray-200 px-1 py-1">
                              {homeOdds ? (
                                isScoreFull(homeScore) ? (
                                  <div className="h-7 flex items-center justify-center border border-gray-300 rounded font-bold w-full" style={{fontSize:'9px', minWidth:60, background:'#d1d5db', color:'#6b7280'}}>{t.matches.soldOut}</div>
                                ) : (
                                  <Input
                                    type="number" step="0.01" min="0" placeholder=""
                                    value={betAmounts[homeScore] || ''}
                                    onChange={(e) => handleAmountChange(homeScore, e.target.value)}
                                    className="h-7 text-center bg-white border-gray-300 rounded w-full"
                                    style={{fontSize:'9px', minWidth:60}}
                                    disabled={isLocked || isSubmitting || isBettingClosed}
                                  />
                                )
                              ) : null}
                            </td>
                          )}
                          <td className="text-center font-bold border border-gray-200 px-1 py-1 bg-white" style={{fontSize:'9px'}}>{homeScore}</td>
                          <td className="text-center font-bold border border-gray-200 px-1 py-1 whitespace-nowrap" style={{color:'#cc003d', fontSize:'9px'}}>
                            {awayOdds ? awayOdds.toFixed(2) : '–'}
                          </td>
                          {canBet && (
                            <td className="border border-gray-200 px-1 py-1">
                              {awayOdds ? (
                                isScoreFull(awayScore) ? (
                                  <div className="h-7 flex items-center justify-center border border-gray-300 rounded font-bold w-full" style={{fontSize:'9px', minWidth:60, background:'#d1d5db', color:'#6b7280'}}>{t.matches.soldOut}</div>
                                ) : (
                                  <Input
                                    type="number" step="0.01" min="0" placeholder=""
                                    value={betAmounts[awayScore] || ''}
                                    onChange={(e) => handleAmountChange(awayScore, e.target.value)}
                                    className="h-7 text-center bg-white border-gray-300 rounded w-full"
                                    style={{fontSize:'9px', minWidth:60}}
                                    disabled={isLocked || isSubmitting || isBettingClosed}
                                  />
                                )
                              ) : null}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ════ BOTTOM/RIGHT TABLE: Draw + AOS ════ */}
              <div className="w-full lg:w-[260px] lg:flex-none overflow-x-auto">
                <table className="border-collapse w-full" style={{fontSize:'9px', minWidth:200}}>
                  <thead>
                    <tr>
                      <th className="text-center font-bold text-white border border-gray-400 px-1 py-1.5" style={{background:'#111', fontSize:'9px'}}>SCORE</th>
                      <th className="text-center font-bold text-white border border-gray-400 px-1 py-1.5 whitespace-nowrap" style={{background:'#00aa44', fontSize:'9px'}}>DRAW-ODD</th>
                      {canBet && <th className="text-center font-bold text-white border border-gray-400 px-1 py-1.5" style={{background:'#00aa44', fontSize:'9px'}}>DRAW</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(['0-0','1-1','2-2','3-3','4-4'] as const).map((drawScore, idx) => {
                      const drawOdds = match.scoreOdds?.[drawScore];
                      const rowBg = idx % 2 === 0 ? '#ffffff' : '#f7f7f7';
                      return (
                        <tr key={drawScore} style={{background: rowBg}}>
                          <td className="text-center font-bold border border-gray-200 px-1 py-1" style={{color:'#00aa44', fontSize:'9px'}}>{drawScore}</td>
                          <td className="text-center font-bold border border-gray-200 px-1 py-1 whitespace-nowrap" style={{color:'#00aa44', fontSize:'9px'}}>
                            {drawOdds ? drawOdds.toFixed(2) : '–'}
                          </td>
                          {canBet && (
                            <td className="border border-gray-200 px-1 py-1">
                              {drawOdds ? (
                                isScoreFull(drawScore) ? (
                                  <div className="h-7 flex items-center justify-center border border-gray-300 rounded font-bold w-full" style={{fontSize:'9px', minWidth:70, background:'#d1d5db', color:'#6b7280'}}>{t.matches.soldOut}</div>
                                ) : (
                                  <Input
                                    type="number" step="0.01" min="0" placeholder=""
                                    value={betAmounts[drawScore] || ''}
                                    onChange={(e) => handleAmountChange(drawScore, e.target.value)}
                                    className={`h-7 text-center bg-white rounded w-full ${
                                      (parseFloat(betAmounts[drawScore] || '0') > 0 && parseFloat(betAmounts[drawScore] || '0') > (scoreLimits[drawScore] ?? 10000))
                                        ? 'border-red-400 bg-red-50' : 'border-gray-300'
                                    }`}
                                    style={{fontSize:'9px', minWidth:70}}
                                    disabled={isLocked || isSubmitting || isBettingClosed}
                                  />
                                )
                              ) : null}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                    {(() => {
                      const aosOdds = match.scoreOdds?.['AOS'];
                      return aosOdds ? (
                        <tr style={{background:'#fff3e0'}}>
                          <td className="text-center font-bold border border-gray-200 px-1 py-1" style={{color:'#e65c00', fontSize:'9px'}}>AOS</td>
                          <td className="text-center font-bold border border-gray-200 px-1 py-1 whitespace-nowrap" style={{color:'#e65c00', fontSize:'9px'}}>
                            {aosOdds.toFixed(2)}
                          </td>
                          {canBet && (
                            <td className="border border-gray-200 px-1 py-1" style={{background:'#fff3e0'}}>
                              {isScoreFull('AOS') ? (
                                <div className="h-7 flex items-center justify-center border border-gray-300 rounded font-bold w-full" style={{fontSize:'9px', minWidth:70, background:'#d1d5db', color:'#6b7280'}}>{t.matches.soldOut}</div>
                              ) : (
                                <Input
                                  type="number" step="0.01" min="0" placeholder=""
                                  value={betAmounts['AOS'] || ''}
                                  onChange={(e) => handleAmountChange('AOS', e.target.value)}
                                  className={`h-7 text-center rounded w-full`}
                                  style={{fontSize:'9px', minWidth:70, borderColor: (parseFloat(betAmounts['AOS'] || '0') > 0 && parseFloat(betAmounts['AOS'] || '0') > (scoreLimits['AOS'] ?? 10000)) ? '#f87171' : '#ffcc80', background:'#fff8f0'}}
                                  disabled={isLocked || isSubmitting || isBettingClosed}
                                />
                              )}
                            </td>
                          )}
                        </tr>
                      ) : null;
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── View-only notice for agents/company ── */}
            {!canBet && (
              <div className="flex items-center gap-2 rounded-md border border-muted bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <span>👁️</span>
                <span>{t.matches.viewOnly}</span>
              </div>
            )}

            {/* ── Betting Summary + Submit (players only) ── */}
            {canBet && (
              <div className="space-y-2 pt-1">
                {totalBetAmount > 0 && (
                  <div className="flex text-sm px-1">
                    <span className="text-muted-foreground">{t.matches.stake}: <span className="font-mono font-semibold text-foreground">{formatCurrency(totalBetAmount)}</span></span>
                  </div>
                )}
                {/* ── Remark input ── */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted-foreground shrink-0">{t.matches.remark}</label>
                  <input
                    type="text"
                    maxLength={15}
                    value={remark}
                    onChange={e => setRemark(e.target.value)}
                    placeholder={t.matches.remarkPlaceholder}
                    className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                  <span className="text-[10px] text-muted-foreground shrink-0">{remark.length}/15</span>
                </div>

                {/* Sold-out info tip: scores at limit are auto-capped, receipt shows sold-out portions */}
                <Button
                  onClick={handleSubmit}
                  disabled={isLocked || isSubmitting || totalBetAmount === 0 || isBettingClosed}
                  className="w-full"
                  size="lg"
                >
                  {isSubmitting ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />{t.matches.placing}</>
                  ) : isBettingClosed ? (
                    <><Clock className="w-4 h-4 mr-2" />{t.matches.closed}</>
                  ) : (
                    <><Target className="w-4 h-4 mr-2" />{t.matches.placeBets}{totalBetAmount > 0 ? ` (${formatCurrency(totalBetAmount)})` : ''}</>
                  )}
                </Button>
              </div>
            )}
          </div>

        ) : (
          <div className="text-center py-4 text-muted-foreground">
            <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{t.matches.noBettingOdds}</p>
            <p className="text-sm md:text-xs">{t.matches.contactAdmin}</p>
          </div>
        )}
      </CardContent>

      {isLocked && (
        <CardFooter className="bg-muted/50 py-2 flex items-center justify-center gap-2">
          <Clock className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm md:text-[10px] text-muted-foreground font-medium">{t.matches.bettingClosed}</span>
        </CardFooter>
      )}
    </Card>

    {/* ── Share Modal ── */}
    {showShareModal && (
      <ShareMatchModal
        match={match}
        imageDataUrl={shareImageUrl}
        onClose={() => { setShowShareModal(false); setShareImageUrl(null); }}
      />
    )}

    {/* ── Bet Receipt ── */}
    {receiptData && (
      <BetReceipt
        data={receiptData}
        onBackToMatches={() => { setReceiptData(null); }}
        onGoToBetHistory={() => { setReceiptData(null); navigate(ROUTE_PATHS.REPORTS); }}
      />
    )}
  </>
  );
}

/**
 * Bet History Table Component
 */
export function BetHistoryTable() {
  const { user } = useAuth();
  const { bets, matches } = useBetting();
  const { t } = useLanguage();
  const [historyReceipt, setHistoryReceipt] = useState<ReceiptData | null>(null);

  // Filter bets for current user
  const userBets = useMemo(() => {
    const filtered = bets.filter(bet => bet.userId === user?.id);
    return filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [bets, user?.id]);

  // Helper function to categorize bets by outcome type
  const categorizeBet = (selection: string) => {
    if (selection === 'AOS') return { category: 'Other', color: 'purple' };
    
    const [home, away] = selection.split('-').map(Number);
    if (isNaN(home) || isNaN(away)) return { category: 'Other', color: 'purple' };
    
    if (home > away) return { category: 'Home Win', color: 'blue' };
    if (home < away) return { category: 'Away Win', color: 'red' };
    return { category: 'Draw', color: 'gray' };
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge variant="secondary">{t.reports.pending}</Badge>;
      case 'WON':
        return <Badge variant="default" className="bg-green-500">Won</Badge>;
      case 'LOST':
        return <Badge variant="destructive">Lost</Badge>;
      case 'CANCELLED':
        return <Badge variant="outline">{t.reports.cancelled}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };


  // Group bets by transactionId (each transaction = one receipt)
  const groupedByTxn = useMemo(() => {
    const groups: Record<string, typeof userBets> = {};
    userBets.forEach(bet => {
      const key = bet.transactionId || bet.id; // fallback to bet id if no txn
      if (!groups[key]) groups[key] = [];
      groups[key].push(bet);
    });
    // Sort each group by createdAt ascending
    Object.values(groups).forEach(g => g.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()));
    return groups;
  }, [userBets]);

  // Helper: build ReceiptData from a transaction group of DB bets
  const buildReceiptDataFromTxn = (txnBets: typeof userBets, match: Match): ReceiptData => {
    // Sort ascending so the FIRST inserted row (which holds sold_out_snapshot) is always [0]
    const sorted = [...txnBets].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    // The first bet in a transaction is the only one that has sold_out_snapshot stored
    // Search ALL rows in case DB ordering differs
    const rowWithSnap = sorted.find(b => b.soldOutSnapshot && (b.soldOutSnapshot as SoldOutItem[]).length > 0);
    const soldOutSnap: SoldOutItem[] = (rowWithSnap?.soldOutSnapshot ?? []) as SoldOutItem[];
    const first = sorted[0];
    return {
      transactionId: first.transactionId || first.id,
      dateTime: new Date(first.createdAt),
      username: (user?.nickname || user?.username) ?? '',
      match,
      bets: sorted.map(b => {
        const snap = soldOutSnap.find(
          s => s.selection === b.selection ||
            (b.selection === 'Any Other Score' && s.selection === 'AOS') ||
            (b.selection === 'AOS' && s.selection === 'Any Other Score')
        );
        return {
          selection: b.selection,
          odds: b.oddsAtTime,
          amount: b.amount,
          soldOutAmount: snap ? snap.soldOutAmount : 0,
        };
      }),
      soldOutItems: soldOutSnap,
      remark: first.remark,
    };
  };

  // Helper: open receipt modal for a transaction group
  const openHistoryReceipt = (txnBets: typeof userBets) => {
    if (txnBets.length === 0) return;
    const match = matches.find(m => m.id === txnBets[0].matchId);
    if (!match) return;
    setHistoryReceipt(buildReceiptDataFromTxn(txnBets, match));
  };

  // Helper: text-only share for a history transaction
  const handleHistoryTextShare = async (txnBets: typeof userBets) => {
    const match = matches.find(m => m.id === txnBets[0]?.matchId);
    if (!match) return;
    const rd = buildReceiptDataFromTxn(txnBets, match);
    const dtStr = format(rd.dateTime, 'yyyy-MM-dd HH:mm:ss');
    const matchDate = format(toMYT(rd.match.startTime), 'yyyy-MM-dd HH:mm:ss') + ' MYT';
    const lines: string[] = [];
    lines.push('===== TRANSACTION RECEIPT =====');
    lines.push(`Serial   : ${rd.transactionId}`);
    lines.push(`Status   : CONFIRMED`);
    lines.push(`Date/Time: ${dtStr}`);
    lines.push(`Nickname : ${rd.username}`);
    if (rd.remark) lines.push(`Remark   : ${rd.remark}`);
    lines.push(`Match    : ${rd.match.homeTeam} vs ${rd.match.awayTeam}`);
    lines.push(`Kick-off : ${matchDate}`);
    lines.push('--------------------------------');
    lines.push('Score       | Odds  | Bet');
    lines.push('--------------------------------');
    rd.bets.forEach(b => {
      const score = b.selection.padEnd(11);
      const odds = b.odds.toFixed(2).padStart(5);
      const amt = b.amount.toFixed(2).padStart(9);
      const so = (b.soldOutAmount ?? 0) > 0 ? ` (+${b.soldOutAmount!.toFixed(2)} sold out)` : '';
      lines.push(`${score} | ${odds} | ${amt}${so}`);
    });
    lines.push('--------------------------------');
    const total = rd.bets.reduce((s, b) => s + b.amount, 0).toFixed(2);
    lines.push(`Total Bet: ${total}`);
    const soldOuts = rd.soldOutItems ?? [];
    if (soldOuts.length > 0) {
      lines.push('================================');
      lines.push('⛔ Sold Out Notice:');
      soldOuts.forEach(si => lines.push(`  ${si.selection}: ${si.soldOutAmount.toFixed(2)} sold out`));
    }
    lines.push('================================');
    const text = lines.join('\n');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Bet Receipt', text });
      } else {
        await navigator.clipboard.writeText(text);
        alert('Receipt copied to clipboard! Paste into WhatsApp / WeChat / Telegram.');
      }
    } catch { /* user cancelled */ }
  };

  // Group bets by match and category (for display table)
  const groupedBets = useMemo(() => {
    const groups: Record<string, {
      matchInfo: string;
      homeWin: typeof userBets;
      draw: typeof userBets;
      awayWin: typeof userBets;
      other: typeof userBets;
    }> = {};

    userBets.forEach(bet => {
      const matchKey = bet.matchId || 'unknown';
      if (!groups[matchKey]) {
        const match = matches.find(m => m.id === matchKey);
        groups[matchKey] = {
          matchInfo: match ? `${match.homeTeam} vs ${match.awayTeam}` : `Match ${matchKey.slice(0, 8)}...`,
          homeWin: [],
          draw: [],
          awayWin: [],
          other: []
        };
      }

      const { category } = categorizeBet(bet.selection);
      switch (category) {
        case 'Home Win':
          groups[matchKey].homeWin.push(bet);
          break;
        case 'Draw':
          groups[matchKey].draw.push(bet);
          break;
        case 'Away Win':
          groups[matchKey].awayWin.push(bet);
          break;
        default:
          groups[matchKey].other.push(bet);
      }
    });

    return groups;
  }, [userBets, matches]);

  return (
    <>
    {/* ── History Receipt Modal ── */}
    {historyReceipt && (
      <BetReceipt
        data={historyReceipt}
        onBackToMatches={() => setHistoryReceipt(null)}
        backLabel="✕ Close"
      />
    )}
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <History className="w-5 h-5" />
          <CardTitle>{t.reports.pageTitle}</CardTitle>
        </div>
        <CardDescription>
          Your recent betting activity organized by outcome type
        </CardDescription>
      </CardHeader>
      <CardContent>
        {userBets.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm">No betting history</p>
            <p className="text-sm md:text-xs">Place your first bet to see history here</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* ── Per-transaction Receipt buttons ── */}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-semibold mb-2">Transactions</p>
              {Object.entries(groupedByTxn).map(([txnId, txnBets]) => {
                const first = txnBets[0];
                const match = matches.find(m => m.id === first.matchId);
                const matchLabel = match ? `${match.homeTeam} vs ${match.awayTeam}` : first.matchId.slice(0, 8);
                const total = txnBets.reduce((s, b) => s + b.amount, 0);
                return (
                  <div key={txnId} className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 gap-3">
                    <div className="min-w-0">
                      <span className="text-xs font-mono text-muted-foreground">{txnId}</span>
                      <span className="mx-2 text-muted-foreground">·</span>
                      <span className="text-xs font-medium">{matchLabel}</span>
                      <span className="mx-2 text-muted-foreground">·</span>
                      <span className="text-xs font-mono">{formatCurrency(total)}</span>
                      <span className="mx-2 text-muted-foreground">·</span>
                      <span className="text-[10px] text-muted-foreground">{format(new Date(first.createdAt), 'dd MMM yy HH:mm')}</span>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-2"
                        onClick={() => openHistoryReceipt(txnBets)}
                        disabled={!match}
                      >
                        {t.reports.receipt}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-2"
                        onClick={() => handleHistoryTextShare(txnBets)}
                        disabled={!match}
                      >
                        {t.reports.textShare.split(' ').slice(0, 2).join(' ')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {Object.entries(groupedBets).map(([matchId, group]) => (
              <div key={matchId} className="border rounded-lg p-4">
                <div className="mb-4">
                  <h4 className="font-medium text-sm text-muted-foreground">{group.matchInfo}</h4>
                </div>
                
                {/* 3-Column Bet History Layout */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Home Win Bets */}
                  <div className="space-y-2">
                    <div className="bg-blue-50 dark:bg-blue-950 p-2 rounded text-center">
                      <div className="text-sm font-semibold text-blue-700 dark:text-blue-300">HOME WIN BETS</div>
                    </div>
                    {group.homeWin.length === 0 ? (
                      <div className="text-xs text-muted-foreground text-center py-2">No home win bets</div>
                    ) : (
                      group.homeWin.map(bet => (
                        <div key={bet.id} className="bg-blue-50/50 dark:bg-blue-950/30 p-2 rounded border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">{bet.selection}</span>
                            {getStatusBadge(bet.status)}
                          </div>
                          <div className="text-sm md:text-xs text-muted-foreground space-y-1">
                            <div className="flex justify-between">
                              <span>Stake:</span>
                              <span className="font-mono">{formatCurrency(bet.amount)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Odds:</span>
                              <span className="font-mono">{bet.oddsAtTime.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Potential:</span>
                              <span className="font-mono">{formatCurrency(bet.amount * bet.oddsAtTime)}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {format(new Date(bet.createdAt), 'MMM dd, HH:mm')}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Draw & Other Bets */}
                  <div className="space-y-2">
                    <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded text-center">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">DRAW & AOS BETS</div>
                    </div>
                    {[...group.draw, ...group.other].length === 0 ? (
                      <div className="text-xs text-muted-foreground text-center py-2">No draw/AOS bets</div>
                    ) : (
                      [...group.draw, ...group.other].map(bet => (
                        <div key={bet.id} className={`p-2 rounded border ${
                          bet.selection === 'AOS' 
                            ? 'bg-purple-50/50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800'
                            : 'bg-gray-50/50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700'
                        }`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">
                              {bet.selection === 'AOS' ? 'Any Other Score' : bet.selection}
                            </span>
                            {getStatusBadge(bet.status)}
                          </div>
                          <div className="text-sm md:text-xs text-muted-foreground space-y-1">
                            <div className="flex justify-between">
                              <span>Stake:</span>
                              <span className="font-mono">{formatCurrency(bet.amount)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Odds:</span>
                              <span className="font-mono">{bet.oddsAtTime.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Potential:</span>
                              <span className="font-mono">{formatCurrency(bet.amount * bet.oddsAtTime)}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {format(new Date(bet.createdAt), 'MMM dd, HH:mm')}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Away Win Bets */}
                  <div className="space-y-2">
                    <div className="bg-red-50 dark:bg-red-950 p-2 rounded text-center">
                      <div className="text-sm font-semibold text-red-700 dark:text-red-300">AWAY WIN BETS</div>
                    </div>
                    {group.awayWin.length === 0 ? (
                      <div className="text-xs text-muted-foreground text-center py-2">No away win bets</div>
                    ) : (
                      group.awayWin.map(bet => (
                        <div key={bet.id} className="bg-red-50/50 dark:bg-red-950/30 p-2 rounded border border-red-200 dark:border-red-800">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium">{bet.selection}</span>
                            {getStatusBadge(bet.status)}
                          </div>
                          <div className="text-sm md:text-xs text-muted-foreground space-y-1">
                            <div className="flex justify-between">
                              <span>Stake:</span>
                              <span className="font-mono">{formatCurrency(bet.amount)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Odds:</span>
                              <span className="font-mono">{bet.oddsAtTime.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Potential:</span>
                              <span className="font-mono">{formatCurrency(bet.amount * bet.oddsAtTime)}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {format(new Date(bet.createdAt), 'MMM dd, HH:mm')}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Summary Statistics */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div className="space-y-1">
                  <div className="text-sm md:text-2xl font-bold text-blue-600">
                    {userBets.filter(bet => categorizeBet(bet.selection).category === 'Home Win').length}
                  </div>
                  <div className="text-sm md:text-xs text-muted-foreground">Home Win Bets</div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm md:text-2xl font-bold text-gray-600">
                    {userBets.filter(bet => ['Draw', 'Other'].includes(categorizeBet(bet.selection).category)).length}
                  </div>
                  <div className="text-sm md:text-xs text-muted-foreground">Draw & AOS Bets</div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm md:text-2xl font-bold text-red-600">
                    {userBets.filter(bet => categorizeBet(bet.selection).category === 'Away Win').length}
                  </div>
                  <div className="text-sm md:text-xs text-muted-foreground">Away Win Bets</div>
                </div>
                <div className="space-y-1">
                  <div className="text-sm md:text-2xl font-bold">
                    {formatCurrency(userBets.reduce((sum, bet) => sum + bet.amount, 0))}
                  </div>
                  <div className="text-sm md:text-xs text-muted-foreground">Total Staked</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
}

/**
 * Completed match read-only card for Match History view
 */
function CompletedMatchCard({ match }: { match: Match }) {
  const startTime = toMYT(match.startTime);
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      <Card className="border-border/50 bg-muted/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5 flex-wrap">
              {match.matchNumber && (
                <Badge variant="outline" className="font-mono text-xs bg-primary/10 text-primary border-primary/30">
                  {match.matchNumber}
                </Badge>
              )}
              <Badge variant="secondary" className="font-mono text-xs">
                {format(startTime, 'dd MMM, HH:mm')} MYT
              </Badge>
              <Badge
                variant={match.status === MatchStatus.COMPLETED ? 'outline' : 'destructive'}
                className="text-xs"
              >
                {match.status === MatchStatus.COMPLETED ? '✅ Completed' : match.status}
              </Badge>
            </div>
          </div>
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-center">
            <div className="flex flex-col items-center gap-1.5">
              <CountryFlag country={match.homeTeam} size={36} />
              <span className="font-semibold text-sm leading-tight line-clamp-2">{match.homeTeam}</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              {match.result ? (
                <span className="text-base font-bold text-foreground">{match.result}</span>
              ) : (
                <span className="text-xs font-bold text-muted-foreground">VS</span>
              )}
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <CountryFlag country={match.awayTeam} size={36} />
              <span className="font-semibold text-sm leading-tight line-clamp-2">{match.awayTeam}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/**
 * Compact Match Summary Card — clicking opens full betting dialog
 */
function MatchSummaryCard({
  match,
  onOpenBet,
  onEdit,
  isLocked,
}: {
  match: Match;
  onOpenBet: () => void;
  onEdit: () => void;
  isLocked: boolean;
}) {
  const { isCompany } = useAuth();
  const { t } = useLanguage();
  const startTime = toMYT(match.startTime); // display time (MYT = UTC+8)
  // DB stores UTC; compare directly with Date.now() minus 2 min buffer
  const bettingCutoff = new Date(new Date(match.startTime).getTime() - 2 * 60 * 1000);
  const [timeUntilCutoff, setTimeUntilCutoff] = useState('');
  const [isBettingClosed, setIsBettingClosed] = useState(false);

  // ── Share state ──
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState<string | null>(null);

  const handleShare = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // prevent card click (open bet)
    setShareImageUrl(null); // show spinner while flags load
    setShowShareModal(true);
    generateShareCanvas(match).then(dataUrl => setShareImageUrl(dataUrl));
  }, [match]);

  // Countdown timer
  useEffect(() => {
    const update = () => {
      const now = new Date();
      const diff = bettingCutoff.getTime() - now.getTime();
      if (diff <= 0) {
        setIsBettingClosed(true);
        setTimeUntilCutoff('Closed');
      } else {
        setIsBettingClosed(false);
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        if (mins > 60) {
          const hrs = Math.floor(mins / 60);
          setTimeUntilCutoff(`${hrs}h ${mins % 60}m`);
        } else if (mins > 0) {
          setTimeUntilCutoff(`${mins}m ${secs}s`);
        } else {
          setTimeUntilCutoff(`${secs}s`);
        }
      }
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [bettingCutoff]);

  const oddsCount = match.scoreOdds
    ? Object.values(match.scoreOdds).filter(o => o > 0).length
    : 0;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        className={cn(
          'group cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 border-border/60 bg-card select-none',
          isLocked && 'opacity-70'
        )}
        onClick={onOpenBet}
      >
        <CardContent className="p-4">
          {/* Top row: date badge + status + edit button */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5 flex-wrap">
              {match.matchNumber && (
                <Badge variant="outline" className="font-mono text-xs bg-primary/10 text-primary border-primary/30">
                  {match.matchNumber}
                </Badge>
              )}
              <Badge
                variant={match.status === MatchStatus.LIVE ? 'destructive' : 'secondary'}
                className="font-mono text-xs"
              >
                {match.status === MatchStatus.LIVE ? 'LIVE' : format(startTime, 'dd MMM, HH:mm') + ' MYT'}
              </Badge>
              <Badge
                variant={isBettingClosed ? 'destructive' : 'outline'}
                className={cn('text-xs', isBettingClosed && 'animate-pulse')}
              >
                <Clock className="w-3 h-3 mr-1" />
                {isBettingClosed ? t.matches.bettingClosed : `${timeUntilCutoff}`}
              </Badge>
              {oddsCount > 0 && (
                <Badge variant="outline" className="text-xs">
                  <Target className="w-3 h-3 mr-1" />
                  {oddsCount} odds
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-0.5">
              {/* Share button — visible to all */}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-primary"
                title="Share match odds"
                onClick={handleShare}
              >
                <Share2 className="h-3.5 w-3.5" />
              </Button>
              {isCompany() && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  onClick={(e) => { e.stopPropagation(); onEdit(); }}
                >
                  <Edit3 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          {/* Teams */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-center">
            <div className="flex flex-col items-center gap-1.5">
              <CountryFlag country={match.homeTeam} size={44} />
              <span className="font-semibold text-sm leading-tight line-clamp-2">{match.homeTeam}</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs font-bold text-muted-foreground">VS</span>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <CountryFlag country={match.awayTeam} size={44} />
              <span className="font-semibold text-sm leading-tight line-clamp-2">{match.awayTeam}</span>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-3 pt-3 border-t border-border/40 flex items-center justify-center gap-2 text-xs text-muted-foreground group-hover:text-primary transition-colors">
            {oddsCount === 0 ? (
              <><AlertCircle className="w-3.5 h-3.5" /> No odds set yet</>
            ) : (
              <><ArrowRight className="w-3.5 h-3.5" /> Tap to view &amp; bet</>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Share Modal ── */}
      {showShareModal && (
        <ShareMatchModal
          match={match}
          imageDataUrl={shareImageUrl}
          onClose={() => { setShowShareModal(false); setShareImageUrl(null); }}
        />
      )}

    </motion.div>
  );
}

/**
 * Main Match Betting Panel — cards list ↔ full match betting view
 */
export function MatchBettingPanel() {
  const { user, canPlaceBets, refreshUser, patchUser } = useAuth();
  const { t } = useLanguage();
  const {
    matches,
    submitDirectBets,
    updateMatch,
    isMatchLocked,
  } = useBetting();

  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);
  // Match History toggle
  const [showHistory, setShowHistory] = useState(false);
  // Pre-filled bets for Re-Bet flow
  const [reBetInitial, setReBetInitial] = useState<Record<string, string> | null>(null);
  // Pending re-bet payload waiting for matches to load (stored in a ref so it survives re-renders)
  const pendingReBet = useRef<{ matchId: string; bets: Record<string, string> } | null>(null);

  // On mount: read rebet_payload from sessionStorage into the ref
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('rebet_payload');
      if (raw) {
        sessionStorage.removeItem('rebet_payload');
        pendingReBet.current = JSON.parse(raw);
      }
    } catch { /* ignore */ }
  }, []); // run once on mount only

  // Whenever matches list updates, try to apply the pending re-bet payload
  useEffect(() => {
    if (!pendingReBet.current || matches.length === 0) return;
    const payload = pendingReBet.current;
    const target = matches.find(m => m.id === payload.matchId);
    if (target) {
      pendingReBet.current = null; // consume
      setSelectedMatch(target);
      setReBetInitial(payload.bets);
    }
  }, [matches]);

  // All upcoming + live matches — only those whose start time is in the future
  // (or whose betting window is still open, i.e. within 2 min of start)
  const upcomingMatches = useMemo(() => {
    const now = new Date();
    return matches
      .filter(m => {
        const start = new Date(m.startTime);
        const isActiveStatus = m.status === MatchStatus.UPCOMING || m.status === MatchStatus.LIVE;
        const isFuture = start > now;
        return isActiveStatus && isFuture;
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [matches]);

  // History = completed/cancelled OR any match whose start time has already passed
  const completedMatches = useMemo(() => {
    const now = new Date();
    return matches
      .filter(m => {
        const start = new Date(m.startTime);
        const isPastStart = start <= now;
        const isDoneStatus = m.status === MatchStatus.COMPLETED || m.status === MatchStatus.CANCELLED;
        // Include if explicitly completed/cancelled, OR if start time is in the past
        return isDoneStatus || isPastStart;
      })
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()); // newest first
  }, [matches]);

  const handleSubmitMatchBets = async (
    matchId: string,
    bets: Array<{ selection: string; amount: number; odds: number }>,
    remark?: string
  ): Promise<any> => {
    if (!canPlaceBets()) throw new Error('Insufficient credit or no permission to place bets');
    if (!user?.id) throw new Error('User not authenticated');
    const transformedBets = bets.map(bet => ({
      matchId,
      selection: bet.selection,
      amount: bet.amount,
      oddsAtTime: bet.odds,
    }));
    // Returns full server response { bets, soldOutItems, hasSoldOut, transactionId, remainingCredit, ... }
    const serverResult = await submitDirectBets(transformedBets, remark);
    // Immediately patch credit in auth state — no DB round-trip, instant sidebar update
    if (serverResult?.remainingCredit !== undefined) {
      patchUser({ availableCreditLimit: serverResult.remainingCredit });
    } else {
      // Fallback: refresh from DB
      await refreshUser();
    }
    window.dispatchEvent(new Event('bet-submitted'));
    return serverResult;
  };

  // ── MATCH DETAIL VIEW ─────────────────────────────────────────────────────
  if (selectedMatch) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="detail"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.22 }}
          className="space-y-4 p-4 md:p-6"
        >
          {/* Back button + match title */}
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedMatch(null)}
              className="flex items-center gap-1.5 shrink-0"
            >
              <ArrowRight className="w-3.5 h-3.5 rotate-180" />
              {t.matches.backToMatches}
            </Button>
            <div className="min-w-0">
              <h2 className="font-bold text-base leading-tight truncate">
                {selectedMatch.homeTeam} <span className="text-muted-foreground font-normal">vs</span> {selectedMatch.awayTeam}
              </h2>
              <p className="text-xs text-muted-foreground">
                {format(toMYT(selectedMatch.startTime), 'EEEE, dd MMM yyyy · HH:mm')} MYT
              </p>
            </div>
          </div>

          {/* Full betting card */}
          <MatchBettingCard
            match={selectedMatch}
            onEdit={() => { setEditingMatch(selectedMatch); }}
            isLocked={isMatchLocked(selectedMatch)}
            initialBets={reBetInitial ?? undefined}
            onSubmitBets={async (matchId, bets, remark) => {
              // Clear re-bet state after first submission attempt
              setReBetInitial(null);
              // Returns full server response; MatchBettingCard handles receipt + soldOutItems
              return handleSubmitMatchBets(matchId, bets, remark);
            }}
          />
        </motion.div>
      </AnimatePresence>
    );
  }

  // ── CARDS LIST VIEW ────────────────────────────────────────────────────────
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="list"
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 40 }}
        transition={{ duration: 0.22 }}
        className="space-y-6 p-4 md:p-6"
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <h2 className="text-2xl font-bold tracking-tight">
              {showHistory ? '📋 Match History' : t.nav.matchBetting}
            </h2>
            <p className="text-sm text-muted-foreground">
              {showHistory
                ? 'Matches that have already completed.'
                : 'Select a match to view odds and place bets.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!showHistory && (
              <Badge variant="secondary" className="w-fit">
                {upcomingMatches.length} {t.matches.availableMatches}
              </Badge>
            )}
            <Button
              variant={showHistory ? 'default' : 'outline'}
              size="sm"
              className="flex items-center gap-1.5"
              onClick={() => setShowHistory(h => !h)}
            >
              <History className="w-4 h-4" />
              {showHistory ? '← Back to Matches' : 'Match History'}
            </Button>
          </div>
        </div>

        {showHistory ? (
          /* ── Match History Grid ── */
          completedMatches.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-4">🏆</div>
              <h3 className="font-semibold text-lg mb-1">No completed matches yet</h3>
              <p className="text-sm text-muted-foreground">Matches that have finished will appear here.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <AnimatePresence>
                {completedMatches.map(match => (
                  <CompletedMatchCard key={match.id} match={match} />
                ))}
              </AnimatePresence>
            </div>
          )
        ) : (
          /* ── Upcoming Matches Grid ── */
          upcomingMatches.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-4">⚽</div>
              <h3 className="font-semibold text-lg mb-1">{t.matches.noUpcoming}</h3>
              <p className="text-sm text-muted-foreground">{t.matches.noUpcomingDesc}</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <AnimatePresence>
                {upcomingMatches.map(match => (
                  <MatchSummaryCard
                    key={match.id}
                    match={match}
                    onOpenBet={() => setSelectedMatch(match)}
                    onEdit={() => setEditingMatch(match)}
                    isLocked={isMatchLocked(match)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )
        )}

        {/* Edit Match Dialog (company only) */}
        <EditMatchDialog
          match={editingMatch}
          isOpen={!!editingMatch}
          onClose={() => setEditingMatch(null)}
          onSave={(updated) => {
            updateMatch(updated.id, updated);
            setEditingMatch(null);
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
}